import apiClient from '@/api/client'
import type { ApiResponse } from '@/types/api'
import {
  dailyGameSchema,
  dailyLeaderboardSchema,
  dueItemsSchema,
  finishRunResultSchema,
  practiceRunSchema,
  topicNodeSchema,
  userInterestSchema,
  type Answer,
  type DailyGame,
  type DailyLeaderboard,
  type DueItems,
  type FinishRunResult,
  type ItemReportReason,
  type PlayableGame,
  type PracticeRun,
  type StartRunPayload,
  type TopicNode,
  type UserInterest,
} from '../types'
import type { z } from 'zod'

/**
 * Frontière de validation Zod (comme `ExerciseBlock`/`ChartBlock` côté
 * `features/sessions`) : une réponse serveur qui ne respecte pas la forme
 * attendue échoue tôt, avec un code exploitable par l'appelant, plutôt que de
 * laisser une valeur invalide se propager en silence dans `usePracticeRun`.
 */
function assertShape<T>(schema: z.ZodType<T>, data: unknown, context: string): void {
  const result = schema.safeParse(data)
  if (!result.success) {
    const error = new Error(`Réponse ${context} de forme inattendue.`) as Error & { code: string }
    error.code = 'INVALID_ANSWER_SHAPE'
    throw error
  }
}

/**
 * Client HTTP de `Domains/Practice` (SPEC §12, `routes/practice.php`), testé
 * avec MSW. `reportItem`, `fetchTopics`, `fetchInterests` et
 * `updateInterests` sont les exceptions — leurs routes vivent dans
 * `routes/knowledge.php` — mais les composants qui les appellent
 * (`ReportItemButton`, `InterestPicker`) habitent `practice/`, donc l'appel
 * aussi.
 */
export const practiceApi = {
  /** Mécaniques jouables selon les items disponibles pour l'utilisateur courant. */
  fetchGames: () => apiClient.get<ApiResponse<PlayableGame[]>>('/practice/games'),

  /** Démarre une partie et fige sa composition d'items. */
  startRun: async (payload: StartRunPayload) => {
    const response = await apiClient.post<ApiResponse<PracticeRun>>('/practice/runs', payload)
    assertShape(practiceRunSchema, response.data.data, 'practice.runs')
    return response
  },

  /** Envoi groupé des réponses — un seul appel par manche, jamais un par item (SPEC §8). */
  submitAnswers: (runId: string, answers: Answer[]) =>
    apiClient.post<ApiResponse<null>>(`/practice/runs/${runId}/answers`, { answers }),

  /** Clôture la partie : score, corrections et XP crédités, calculés côté serveur. */
  finishRun: async (runId: string) => {
    const response = await apiClient.post<ApiResponse<FinishRunResult>>(`/practice/runs/${runId}/finish`)
    assertShape(finishRunResultSchema, response.data.data, 'practice.runs.finish')
    return response
  },

  /** Le jeu du jour (SPEC §9, §12) : descripteur à jouer, ou résultat déjà obtenu (`DailyGameDto`). */
  fetchDaily: async () => {
    const response = await apiClient.get<ApiResponse<DailyGame>>('/practice/daily')
    assertShape(dailyGameSchema, response.data.data, 'practice.daily')
    return response
  },

  fetchDailyLeaderboard: async () => {
    const response = await apiClient.get<ApiResponse<DailyLeaderboard>>('/practice/daily/leaderboard')
    assertShape(dailyLeaderboardSchema, response.data.data, 'practice.daily.leaderboard')
    return response
  },

  /**
   * Items dus pour la répétition espacée de l'utilisateur (SPEC §10, §12) —
   * l'état de révision de chaque item (`DueItemsDto`), jamais son `payload` :
   * une liste de révisions qui porterait les questions et leurs réponses
   * serait une fuite. `topicIds` restreint aux items de ces topics ; omis,
   * aucun filtre n'est appliqué.
   *
   * **Désaccord de contrat corrigé (lot S).** Ce client typait jusqu'ici sa
   * réponse en `ApiResponse<PracticeItem[]>` — des items de catalogue avec
   * leur `payload` — alors que le serveur (`DueItemsDto::toArray()`) rend
   * `{ items: [{ item_id, state, reps, ... }], count }`, deux formes sans un
   * seul champ commun. `dueItemsSchema` aligne le client sur ce que le
   * serveur rend réellement.
   */
  fetchDue: async (topicIds?: string[]) => {
    const response = await apiClient.get<ApiResponse<DueItems>>('/practice/due', {
      params: topicIds && topicIds.length > 0 ? { topic_ids: topicIds } : undefined,
    })
    assertShape(dueItemsSchema, response.data.data, 'practice.due')
    return response
  },

  /** Signale un item douteux (SPEC §11.2). Résout sans valeur : `ReportItemButton` ne lit que le succès. */
  reportItem: (itemId: string, payload: { reason: ItemReportReason; comment?: string }): Promise<void> =>
    apiClient.post<ApiResponse<null>>(`/knowledge/items/${itemId}/report`, payload).then(() => undefined),

  /**
   * Arbre complet des topics (SPEC §6.1, §12) — route `Domains/Knowledge`,
   * consommée ici pour la même raison que `reportItem` ci-dessus :
   * `InterestPicker` habite `features/practice/`.
   */
  fetchTopics: async () => {
    const response = await apiClient.get<ApiResponse<TopicNode[]>>('/topics')
    response.data.data.forEach((node) => assertShape(topicNodeSchema, node, 'topics'))
    return response
  },

  /** Centres d'intérêt de l'utilisateur authentifié (SPEC §6.2, §12). */
  fetchInterests: async () => {
    const response = await apiClient.get<ApiResponse<UserInterest[]>>('/me/interests')
    response.data.data.forEach((interest) => assertShape(userInterestSchema, interest, 'me.interests'))
    return response
  },

  /** Remplace intégralement la sélection de centres d'intérêt (SPEC §12 : `{ topic_ids }`). */
  updateInterests: (topicIds: string[]) =>
    apiClient.put<ApiResponse<UserInterest[]>>('/me/interests', { topic_ids: topicIds }),
}
